/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { useEffect, useRef, useState } from 'react';
import { firstKey, getUrlParams, parseJson, userHasRole } from '../utils';
import { equipmentWasRegistered, getEquipmentBySN, getEquipmentDisplayInfoBySN } from '../_shared/services/manage-equipment.service';
import { get, set, size } from 'lodash';
import { Equipment, EquipmentRecord } from '../_shared/interfaces/equipment';
import { User } from '../_shared/interfaces/user';
import { message } from 'antd';
import styles from './styles';
import { Loading } from '../_shared/icon';
import { Auth } from './auth';
import { isLoggedIn } from '../utils';
import { useSelector } from 'react-redux';
import { AppState } from '../app.state';
import { EqRegistrationForm } from './eq';
import { OrgCreate } from './org';
import colors from '../_shared/colors';
import { withRouter } from 'react-router-dom';
import { RouterProps } from 'react-router';
import { instance } from '../_shared/services/instance';

export const REG_KEY = 'EQRegistration';

interface IProps extends RouterProps {

}

interface IState {
  ls: {
    //what gets sent over in the EQRegistration localstorage object
    equipment?: Equipment;
  }

  equipmentToLoad: Equipment[];

  loadedEquipment: {
    [key: string]: EquipmentRecord;
  }

  loading: boolean;

  authState: any;

  // AER-1755: display-only copy of the scanned unit, resolved before the user
  // signs in so the purple header bar can render on Login / New User Sign Up.
  // Never feeds the registration validation in loadEquipment().
  previewEquipment: EquipmentRecord[];
}

let displayedMessage = false;

// AER-1589: an "invalid" equipment record is one we should refuse to register
// or display — the backend flags these via showOnDash=false / isValid=false.
const isInvalidEquipment = (eq: EquipmentRecord | null | undefined) => {
  if (!eq) return false;
  if (get(eq, 'isValid') === false) return true;
  if (get(eq, 'showOnDash') === false) return true;
  return false;
};

// AER-1589: a logged-in user can view the equipment-details page when they
// are a DentalEz admin/staff/manufacturer (roles 0/1/5/7) or their org matches
// the equipment's dentalOrgId / serviceOrgId. Mirrors the native QR-scan flow.
const userCanViewEquipment = (user: User | null | undefined, eq: EquipmentRecord) => {
  const role = get(user, 'role') as number | undefined;
  if (role !== undefined && [0, 1, 5, 7].includes(role)) return true;
  const orgId = get(user, 'orgId') as string | undefined;
  if (!orgId) return false;
  if (get(eq, 'dentalOrgId') === orgId) return true;
  if (get(eq, 'serviceOrgId') === orgId) return true;
  return false;
};

export type RegistrationFormType = 'auth' | 'verify' | 'org' | 'eq' | 'a1';

// Common onSubmit hook - can be imported and called by all registration forms
export const onRegistrationSubmit = async (formType: RegistrationFormType, data: any) => {
  // Only send to Zoho for equipment registrations
  if (data && data.equipment) {
    try {
      await equipmentWasRegistered(data.equipment);
    } catch (err) {
      console.error('Failed to send equipment registration to Zoho:', err);
    }
  }
};

export const EQRegistration = withRouter((props: IProps) => {
  const user = useSelector(s => get(s, 'auth.user'));
  const loggedIn = !!useSelector(s => isLoggedIn(s as AppState));
  const hasOrg = get(user, 'orgId');
  // AER-1755: the bootstrap token minted by loadToken() on app start. It exists
  // before sign-in and is all the display-only SN lookup below needs.
  const token = useSelector(s => get(s, 'auth.token')) as string | undefined;

  const [state, setState] = useState({
    ls: {
      //gets loaded from what is in the EQRegistration localstorage object, along with whatever else. Only really looking for "equipment"
      equipment: undefined,
    },

    equipmentToLoad: [],

    loadedEquipment: {},
    loading: true,

    authState: {},

    previewEquipment: [],
  } as IState);

  const getEqFromLocalStorage = (): Promise<Equipment[]> => {
    return new Promise((res) => {
      const json = localStorage.getItem(REG_KEY);

      const params = getUrlParams(window.location.href);
      const obj = params && params.serialnumber ? { equipment: params } : parseJson(json as string);

      if (obj.equipment) {
        localStorage.setItem(REG_KEY, JSON.stringify(obj));
      }

      const equipmentToLoad = obj.equipment ? [obj.equipment] : [];
      set(state, 'equipmentToLoad', equipmentToLoad);
      setState(s => ({ ...s, ls: obj, equipmentToLoad }))
      // AER-1589: resolve with the freshly-loaded equipment list so callers
      // don't have to read it back through `state.equipmentToLoad` (the
      // setState above hasn't propagated yet in the current closure).
      setTimeout(() => res(equipmentToLoad), 250);
    })
  }

  const loadEquipment = async (items?: Equipment[]) => {
    try {
      setState(s => ({ ...s, loading: true }))

      const equipmentToLoad = items || state.equipmentToLoad;

      const loadedEquipment = {};
      // AER-1589: when a QR-scan deep link drops the user on
      // /eqRegistration?serialnumber=...&activationcode=..., refuse to load
      // the form for invalid or already-registered equipment. Mirrors the
      // native (camera-screen) validation in aeras-native's WifiPair flow,
      // and also covers users who hit the URL directly in the browser.
      let blocked = false;
      let redirectEq: EquipmentRecord | null = null;
      await Promise.all(equipmentToLoad.map(async e => {
        // Pass activationcode so the server can disambiguate between two
        // active records sharing the same equipmentSN.
        const eq = await getEquipmentBySN(e.serialnumber as string, e.activationcode as string);

        if (!eq) {
          return message.error(`No equipment with that Serial Number exists.`)
        }

        if (isInvalidEquipment(eq)) {
          blocked = true;
          message.error('Equipment serial number invalid. Please contact support.');
          return;
        }

        if (get(eq, 'dentalOrgId')) {
          blocked = true;
          message.error('Equipment has already been registered.');
          if (userCanViewEquipment(user, eq)) {
            redirectEq = eq;
          }
          return;
        }

        set(loadedEquipment, e.serialnumber as string, { ...eq, activationcode: e.activationcode })
      }))

      if (blocked) {
        // Drop the URL params + persisted registration state so a refresh
        // doesn't replay the rejected scan.
        localStorage.removeItem(REG_KEY);
        try {
          window.history.replaceState({}, '', '/eqRegistration');
        } catch (_e) { /* noop */ }
        setState(s => ({ ...s, loadedEquipment: {}, equipmentToLoad: [], previewEquipment: [] }));
        if (redirectEq) {
          props.history.push('/dashboard/equipmentDetails', { equipment: redirectEq });
        }
        return;
      }

      setState(s => ({ ...s, loadedEquipment }));
    } catch (err) {
      message.error(err);
    } finally {
      setState(s => ({ ...s, loading: false }))
    }
  }

  const renderLoadedEquipment = (style = {} as any) => {
    // AER-1755: `loadedEquipment` is only populated once the user is signed in
    // (see the AER-1589 note below), which is why the purple header bar used to
    // appear for the first time on Organization Details. Fall back to the
    // display-only preview so Login and New User Sign Up show the same bar for
    // a QR-scanned unit, and render nothing at all when no unit was scanned so
    // the bar can never show up blank.
    const items = size(state.loadedEquipment)
      ? Object.keys(state.loadedEquipment).map(k => state.loadedEquipment[k])
      : state.previewEquipment;

    if (!size(items)) {
      return null;
    }

    return (
      <div style={style}>
        {items.map(e => <EQInfo key={get(e, 'equipmentSN')} equipment={e} />)}
      </div>
    )
  }

  // AER-1755: resolve the scanned unit's serial number + model for display only,
  // before the user signs in, so the purple header bar is on the Login and New
  // User Sign Up screens too. AER-1589 moved the real (validating) lookup behind
  // the sign-in gate because a pre-login 401 on the shared axios instance wipes
  // localforage and force-reloads the page; this lookup therefore waits for the
  // bootstrap token, uses an interceptor-free request, and swallows every
  // failure — the worst case is simply no bar. It never blocks or unblocks a
  // registration: loadEquipment() remains the only validation path.
  const previewKey = state.equipmentToLoad
    .map(e => `${get(e, 'serialnumber', '')}|${get(e, 'activationcode', '')}`)
    .join(',');
  useEffect(() => {
    if (loggedIn || !token) return;

    const items = state.equipmentToLoad.filter(e => !!get(e, 'serialnumber'));
    if (!size(items)) {
      setState(s => (size(s.previewEquipment) ? { ...s, previewEquipment: [] } : s));
      return;
    }

    let cancelled = false;
    (async () => {
      const found = await Promise.all(items.map(async e => {
        try {
          return await getEquipmentDisplayInfoBySN(
            get(e, 'serialnumber') as string,
            token,
            get(e, 'activationcode') as string
          );
        } catch (_e) {
          return null;
        }
      }));

      if (cancelled) return;

      const previewEquipment = found.filter(e => !!get(e, 'equipmentSN')) as EquipmentRecord[];
      setState(s => ({ ...s, previewEquipment }));
    })();

    return () => { cancelled = true; };
  }, [loggedIn, token, previewKey]);

  // AER-1589: defer the SN lookup until the user is signed in. The
  // /equipment/sn/:sn endpoint requires auth, and previously this effect
  // fired unconditionally on mount: pre-login the request 401s, the global
  // axios interceptor clears localforage and forces a redirect to /login,
  // and even after re-login the closure had captured user=null so the
  // "already registered → redirect to Equipment Details" branch never ran
  // for admins (userCanViewEquipment(null, eq) === false). Gating on
  // [loggedIn, user] guarantees a fresh user reference when loadEquipment
  // runs; the ref keeps it to a single attempt per mount; and we pass the
  // freshly-loaded equipmentToLoad straight into loadEquipment rather than
  // relying on the stale state closure.
  const equipmentBootstrappedRef = useRef(false);
  const equipmentLookupAttemptedRef = useRef(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // First mount: synchronously seed equipmentToLoad from the URL +
      // localStorage so the <Auth> screen can render renderLoadedEquipment.
      let items: Equipment[];
      if (!equipmentBootstrappedRef.current) {
        equipmentBootstrappedRef.current = true;
        items = await getEqFromLocalStorage();
      } else {
        items = state.equipmentToLoad;
      }
      if (cancelled) return;
      if (!loggedIn || !user) return;
      if (equipmentLookupAttemptedRef.current) return;
      equipmentLookupAttemptedRef.current = true;
      loadEquipment(items);
    })();
    return () => { cancelled = true; };
  }, [loggedIn, user]);

  useEffect(() => {
    if (loggedIn && user) {
      const sState = { auth: { user } } as any;

      let passes = true;

      if (userHasRole(5, sState)) {
        props.history.push('/dashboard/manufactureProcess');
        passes = false;
      } else if (userHasRole([6, 7], sState)) {
        props.history.push('/dashboard');
        passes = false;
      }

      if (!passes && !displayedMessage) {
        displayedMessage = true;
        message.error('Equipment Registration is not available on your user account.');
      }
    }
  }, [loggedIn]);

  const showEqRegistration = loggedIn && (hasOrg || userHasRole([0, 1], { auth: { user } } as any));

  return (
    <div css={css(styles.container, (state.authState.showSignup || showEqRegistration || loggedIn) && `justify-content: flex-start;`)}>
      <div style={{ marginTop: 5 }}>
        {state.loading && <Loading />}
      </div>
      {showEqRegistration
        ? <EqRegistrationForm {...state} {...{ renderLoadedEquipment, onRegistrationSubmit }} />
        : loggedIn
          ? <OrgCreate renderLoadedEquipment={renderLoadedEquipment} equipment={firstKey(state.loadedEquipment)} onRegistrationSubmit={onRegistrationSubmit} />
          : <Auth renderLoadedEquipment={renderLoadedEquipment} onStateChange={sa => setState(s => ({ ...s, authState: sa }))} onRegistrationSubmit={onRegistrationSubmit} />}
    </div>
  )
})

const EQInfo = (props: any) => {
  const { equipment } = props;

  const fields = [
    { title: 'Equipment Serial Number', val: get(equipment, 'equipmentSN') },
    { title: 'Model', val: get(equipment, 'modelId') },
  ]

  return (
    <div style={{ backgroundColor: colors.highlight, width: '100%', color: colors.white, padding: 10 }}>
      {/* AER-1755: skip any field we don't have a value for rather than
          printing "Model: undefined" in the header bar. */}
      {fields.filter(f => f.val !== undefined && f.val !== null && f.val !== '')
        .map(f => <div key={f.title}>{`${f.title}`}<b>{`: ${f.val}`}</b></div>)}
    </div>
  )
}